/**
 * Native Google Sign-In — bundled Capacitor builds only.
 *
 * On native there is no browser to redirect to: the OS account picker returns a
 * Google `id_token` directly, which the app POSTs to `/api/auth/google-native`
 * (card C) in exchange for the same opaque `session_id` cookie the web callback
 * sets. No deep links, no PKCE, no bearer token.
 *
 * The web path (`authService.signInWithGoogle` → Supabase `/auth/v1/authorize` →
 * `AuthCallbackPage`) is untouched — callers branch on `isNative` before reaching
 * this module. See `src/docs/GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md` §3/§4/§10 and
 * `src/docs/GOOGLE_OAUTH_SETUP.md` §8.
 */

import { SocialLogin } from '@capgo/capacitor-social-login';
import { GOOGLE_CLIENT_IDS } from '@/config/googleAuth';
import { isIOS } from '@/utils/platform';

export type NativeGoogleAuthErrorCode =
  /** The user dismissed the account picker. Never surface this as an error. */
  | 'USER_CANCELLED'
  /** The picker succeeded but returned no id_token — nothing to exchange. */
  | 'NO_ID_TOKEN'
  /** No WebCrypto in this WebView, so the §8.6 nonce cannot be computed. */
  | 'NONCE_UNSUPPORTED'
  /** Anything else the plugin/SDK threw (misconfigured client ID, no network, …). */
  | 'PLUGIN_ERROR';

export class NativeGoogleAuthError extends Error {
  constructor(
    message: string,
    public code: NativeGoogleAuthErrorCode,
    public cause?: unknown
  ) {
    super(message);
    this.name = 'NativeGoogleAuthError';
  }
}

/**
 * Raised when the id_token exchange **succeeded** (the backend created a session
 * and set the cookie) but the follow-up authenticated request failed — i.e. the
 * session cookie is not being replayed on this platform. That is a transport
 * fault, not a sign-in fault, and conflating the two sends you hunting for an
 * OAuth bug that isn't there (GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md §9.6 flags iOS
 * as the unproven case).
 */
export class NativeSessionVerificationError extends Error {
  constructor(
    public status: number | undefined,
    public serverCode: string | undefined,
    /**
     * The underlying error text. Status 0 means the request never got an HTTP
     * response at all, and then this string is the only thing that says why —
     * so it is carried through to the UI rather than swallowed.
     */
    public detail: string | undefined,
    public cause?: unknown
  ) {
    super('Signed in, but the session could not be verified on this device.');
    this.name = 'NativeSessionVerificationError';
  }
}

/**
 * `initialize` is idempotent for our purposes but not free, so it runs once per
 * app session. A failure clears the memo so a later attempt can retry.
 */
let initializePromise: Promise<void> | null = null;

export const initializeGoogleSignIn = (): Promise<void> => {
  if (!initializePromise) {
    initializePromise = SocialLogin.initialize({
      google: {
        // Android's serverClientId AND the id_token audience on Android.
        webClientId: GOOGLE_CLIENT_IDS.web,
        // iOS only — and the id_token audience on iOS.
        iOSClientId: GOOGLE_CLIENT_IDS.ios,
        iOSServerClientId: GOOGLE_CLIENT_IDS.web,
        // 'online' returns the id_token we need. 'offline' returns only a
        // serverAuthCode, which card C's endpoint does not accept.
        mode: 'online',
      },
    }).catch((error) => {
      initializePromise = null;
      throw error;
    });
  }
  return initializePromise;
};

/** Reset the one-shot initialize memo. Test seam only. */
export const resetGoogleSignInInitialization = (): void => {
  initializePromise = null;
};

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * The §8.6 nonce contract, and the single most likely cause of a "works on the
 * device, 401 from the backend" bug:
 *
 *   hashed → Google (the plugin passes it through verbatim on both platforms;
 *            it does NOT hash for you, and Google echoes it into the `nonce` claim)
 *   raw    → the backend (Supabase hashes it itself and compares)
 *
 * Get it backwards and the exchange fails with `401 INVALID_ID_TOKEN`, which is
 * indistinguishable from a bad audience.
 */
export const generateNonce = async (): Promise<{ raw: string; hashed: string }> => {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle || !webCrypto.getRandomValues) {
    throw new NativeGoogleAuthError(
      'This device cannot generate a secure sign-in nonce (WebCrypto unavailable).',
      'NONCE_UNSUPPORTED'
    );
  }

  const raw = toHex(webCrypto.getRandomValues(new Uint8Array(32)));
  const digest = await webCrypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return { raw, hashed: toHex(new Uint8Array(digest)) };
};

/**
 * The plugin rejects with `code === 'USER_CANCELLED'` on dismissal (verified in
 * its Kotlin `GoogleProvider.java` and Swift `SocialLoginPlugin.swift` at
 * 8.3.39). The message check is a belt-and-braces fallback: mistaking a genuine
 * failure for a cancel costs a missing toast, while mistaking a cancel for a
 * failure shows the user an error they did not cause.
 */
const isUserCancellation = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'USER_CANCELLED') return true;
  const message = (error as { message?: string } | null)?.message?.toLowerCase() ?? '';
  return message.includes('cancel') || message.includes('access_denied');
};

/**
 * Launch the OS Google account picker and return the assertion to exchange.
 *
 * @returns the Google `id_token` and the **raw** (unhashed) nonce to POST with it.
 */
export const getGoogleIdToken = async (): Promise<{ idToken: string; nonce: string }> => {
  await initializeGoogleSignIn();
  const { raw, hashed } = await generateNonce();

  let response: Awaited<ReturnType<typeof SocialLogin.login<'google'>>>;
  try {
    response = await SocialLogin.login({
      provider: 'google',
      options: {
        nonce: hashed,
        // iOS-only guard: without this the plugin takes its
        // `restorePreviousSignIn()` shortcut whenever a previous sign-in exists,
        // which returns a cached id_token minted with a DIFFERENT nonce (or
        // none) — the backend would then 401. On Android this flag only affects
        // the 'bottom' UI, which we don't use, so it is a no-op there.
        forcePrompt: isIOS,
      },
    });
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new NativeGoogleAuthError('Google sign-in was cancelled.', 'USER_CANCELLED', error);
    }
    throw new NativeGoogleAuthError(
      (error as { message?: string } | null)?.message || 'Google sign-in failed.',
      'PLUGIN_ERROR',
      error
    );
  }

  const result = response.result;
  const idToken = result.responseType === 'online' ? result.idToken : null;
  if (!idToken) {
    throw new NativeGoogleAuthError(
      'Google did not return an identity token.',
      'NO_ID_TOKEN',
      response
    );
  }

  return { idToken, nonce: raw };
};

/**
 * How the UI should react to a failed native sign-in. Keeping this a pure
 * function of the thrown error means the page just renders the outcome, and the
 * mapping itself is unit-testable without a device.
 *
 * `no_account` is the account gate's expected rejection
 * (BETA_GATED_OAUTH_PLAN.md §2) — graceful copy, not a failure toast.
 */
export type NativeGoogleOutcome =
  | { kind: 'cancelled' }
  | { kind: 'no_account'; message: string }
  | { kind: 'error'; title: string; message: string };

export const mapNativeGoogleError = (error: unknown): NativeGoogleOutcome => {
  if (error instanceof NativeSessionVerificationError) {
    // Deliberately distinct copy: Google *did* sign the user in. Saying "sign-in
    // failed" here would be a lie and would misdirect the next person debugging it.
    return {
      kind: 'error',
      title: 'Signed in, but the session did not stick',
      message: `Google accepted the sign-in, but this device could not keep the session (${error.status ?? 'network'}/${error.serverCode ?? 'no-code'}${error.detail ? `: ${error.detail.slice(0, 120)}` : ''}). Please report this.`,
    };
  }

  if (error instanceof NativeGoogleAuthError) {
    switch (error.code) {
      case 'USER_CANCELLED':
        return { kind: 'cancelled' };
      case 'NONCE_UNSUPPORTED':
        return {
          kind: 'error',
          title: 'Google sign-in unavailable',
          message: 'This device could not start a secure Google sign-in. Please sign in with your email instead.',
        };
      default:
        return {
          kind: 'error',
          title: 'Google sign-in failed',
          message: 'We could not complete Google sign-in. Please try again.',
        };
    }
  }

  // Everything below is an ApiError from POST /api/auth/google-native (§10.1).
  const status = (error as { status?: number } | null)?.status;
  const code = (error as { data?: { code?: string } } | null)?.data?.code;

  if (code === 'NO_ACCOUNT') {
    return {
      kind: 'no_account',
      message: 'No account found for that Google login — sign up first.',
    };
  }
  if (status === 429) {
    return {
      kind: 'error',
      title: 'Too many attempts',
      message: 'Too many sign-in attempts. Please wait a few minutes and try again.',
    };
  }
  if (code === 'INVALID_ID_TOKEN' || code === 'MISSING_ID_TOKEN') {
    return {
      kind: 'error',
      title: 'Google sign-in failed',
      message: 'Google could not verify that sign-in. Please try again.',
    };
  }
  // Unrecognised failure. Carry the status/code in the copy — the generic version
  // of this message cost a full debugging round-trip on iOS, because it looked
  // identical whether the picker, the exchange or the session check had failed.
  return {
    kind: 'error',
    title: 'Google sign-in failed',
    message: `Something went wrong signing you in (${status ?? 'network'}/${code ?? 'no-code'}). Please try again.`,
  };
};
